package com.isaias.fyattravel.web.pages.home

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.fetch.RequestInit
import org.w3c.fetch.Headers
import com.isaias.fyattravel.web.data.WebProduct
import com.isaias.fyattravel.web.data.WebCategory
import com.isaias.fyattravel.web.components.ProductBookingForm
import com.isaias.fyattravel.web.layout.rememberResponsiveState
import com.isaias.fyattravel.web.i18n.WebStringKey
import com.isaias.fyattravel.web.i18n.WebStrings
import com.isaias.fyattravel.web.theme.ThemeColors
import com.isaias.fyattravel.web.canonicalCategoryKey

private const val FUNCTIONS_BASE = "https://us-central1-fyattravelnew.cloudfunctions.net"

// Known airlines with display names and brand colors
private val KNOWN_AIRLINES = mapOf(
    "ET" to Triple("Ethiopian", "#009A44", "ET"),
    "EK" to Triple("Emirates", "#D71921", "EK"),
    "FZ" to Triple("flydubai", "#E0001B", "FZ"),
    "UR" to Triple("Uganda Air", "#FFCD00", "UR"),
    "KQ" to Triple("Kenya Airways", "#CC0001", "KQ"),
    "WB" to Triple("RwandAir", "#00A0DC", "WB"),
    "QR" to Triple("Qatar Airways", "#5C0632", "QR"),
    "TK" to Triple("Turkish", "#E81932", "TK"),
    "EY" to Triple("Etihad", "#B9993E", "EY"),
    "BA" to Triple("British Airways", "#2B5FDA", "BA"),
    "LH" to Triple("Lufthansa", "#0D2481", "LH"),
    "AF" to Triple("Air France", "#002157", "AF"),
    "KL" to Triple("KLM", "#00A1DE", "KL"),
)

/**
 * Home page with hero section, categories, and flight offers
 */
@Composable
fun HomePage(onNavigate: (String) -> Unit) {
    val responsive = rememberResponsiveState()
    // Flight offers state
    var flightOffers by remember { mutableStateOf(emptyList<WebProduct>()) }
    var offersLoading by remember { mutableStateOf(true) }
    
    // Categories state
    var categories by remember { mutableStateOf(emptyList<WebCategory>()) }
    var categoriesLoading by remember { mutableStateOf(true) }

    // Fetch flight offers — discover exact category name from listProductCategories first
    LaunchedEffect(Unit) {
        fun parseProducts(text: String): List<WebProduct> {
            return try {
                val obj = kotlin.js.JSON.parse<dynamic>(text)
                val ok = (obj.success as Boolean?) == true
                if (!ok) return emptyList()
                val arr = (obj.products as Array<dynamic>?) ?: emptyArray()
                arr.map { d ->
                    WebProduct(
                        id = d.id as String? ?: "",
                        title = d.title as String?,
                        description = d.description as String?,
                        thumbnail = d.thumbnail as String?,
                        category = d.category as String?,
                        createdAt = (d.createdAt as Double?)?.toLong(),
                        priceUsd = (d.priceUsd as Double?)?.toInt(),
                        priceUgx = (d.priceUgx as Double?)?.toInt(),
                        badge = d.badge as String?,
                        discountPercent = (d.discountPercent as Double?)?.toInt(),
                        airlineCode = d.airlineCode as String?,
                        originalPriceUsd = (d.originalPriceUsd as Double?)?.toInt(),
                        originalPriceUgx = (d.originalPriceUgx as Double?)?.toInt()
                    )
                }.filter { it.id.isNotBlank() }
            } catch (t: dynamic) {
                console.error("Failed to parse products", t)
                emptyList()
            }
        }

        fun fetchByCategory(catName: String, limit: Int, onDone: (List<WebProduct>) -> Unit) {
            val bodyJson = """{"category":"$catName","limit":$limit}"""
            window.fetch(
                "$FUNCTIONS_BASE/listProductsByCategory",
                RequestInit(
                    method = "POST",
                    headers = Headers().also { h -> h.append("Content-Type", "application/json") },
                    body = bodyJson
                )
            ).then { resp ->
                resp.text().then { text -> onDone(parseProducts(text)) }
            }.catch { _ -> onDone(emptyList()) }
        }

        // Step 1: Ask the backend what exact category names exist
        window.fetch("$FUNCTIONS_BASE/listProductCategories")
            .then { resp -> resp.text().then { text ->
                try {
                    val obj = kotlin.js.JSON.parse<dynamic>(text)
                    val ok = (obj.success as Boolean?) == true
                    if (ok) {
                        val arr = (obj.categories as Array<dynamic>?) ?: emptyArray()
                        val catNames = arr.map { d -> d.name as String? ?: "" }.filter { it.isNotBlank() }
                        val exactFlightName = catNames.firstOrNull { canonicalCategoryKey(it) == "flight" }

                        if (exactFlightName != null) {
                            // Step 2: Fetch products using the EXACT name the backend recognizes
                            fetchByCategory(exactFlightName, 6) { items ->
                                if (items.isNotEmpty()) flightOffers = items
                                offersLoading = false
                            }
                        } else {
                            // No flight category found — fallback to hardcoded names
                            fetchByCategory("flight", 6) { items ->
                                if (items.isNotEmpty()) {
                                    flightOffers = items
                                    offersLoading = false
                                } else {
                                    fetchByCategory("flightOffers", 6) { items2 ->
                                        if (items2.isNotEmpty()) flightOffers = items2
                                        offersLoading = false
                                    }
                                }
                            }
                        }
                    } else {
                        offersLoading = false
                    }
                } catch (t: dynamic) {
                    console.error("Failed to discover flight category", t)
                    offersLoading = false
                }
            }}
            .catch { _ ->
                // Network error on listProductCategories — fallback to hardcoded names
                fetchByCategory("flight", 6) { items ->
                    if (items.isNotEmpty()) {
                        flightOffers = items
                        offersLoading = false
                    } else {
                        fetchByCategory("flightOffers", 6) { items2 ->
                            if (items2.isNotEmpty()) flightOffers = items2
                            offersLoading = false
                        }
                    }
                }
            }
    }
    
    // Fetch categories
    LaunchedEffect(Unit) {
        window.fetch(
            "$FUNCTIONS_BASE/listCategories",
            RequestInit(
                method = "POST",
                headers = Headers().also { h -> h.append("Content-Type", "application/json") },
                body = "{}"
            )
        ).then { resp ->
            resp.text().then { text ->
                try {
                    val obj = kotlin.js.JSON.parse<dynamic>(text)
                    val ok = (obj.success as Boolean?) == true
                    if (ok) {
                        val arr = (obj.categories as Array<dynamic>?) ?: emptyArray()
                        // Hidden categories (not shown on web)
                        val hiddenCategories = setOf("visa", "visa services", "transfer", "transfers", "airport transfer", "airport transfers")
                        
                        categories = arr.map { d ->
                            val rawName = d.name as String? ?: ""
                            // Rename hotel variants to "Hotel & GuestHouse"
                            val displayName = when (rawName.trim().lowercase()) {
                                "hotel", "hotels", "stay", "stays", "guesthouse", "guest house" -> "Hotel & GuestHouse"
                                else -> rawName
                            }
                            WebCategory(
                                name = displayName,
                                count = (d.count as Double?)?.toInt() ?: 0
                            )
                        }.filter { 
                            it.name.isNotBlank() && 
                            it.count > 0 && 
                            it.name.trim().lowercase() !in hiddenCategories
                        }
                    }
                } catch (t: dynamic) {
                    console.error("Failed to load categories", t)
                } finally {
                    categoriesLoading = false
                }
            }
        }.catch {
            categoriesLoading = false
        }
    }

    // Welcome section
    H1({
        style {
            fontSize(if (responsive.isMobile) 1.7.em else if (responsive.isTablet) 2.0.em else 2.4.em)
            fontWeight("900")
            marginBottom(0.35.em)
            color(ThemeColors.textPrimary)
        }
    }) { Text(WebStrings.t(WebStringKey.HOME_SUBTITLE)) }

    P({
        style {
            fontSize(if (responsive.isMobile) 0.95.em else 1.05.em)
            color(ThemeColors.textMuted)
            marginBottom(1.25.em)
        }
    }) { Text(WebStrings.t(WebStringKey.HOME_HELP_TEXT)) }

    // Flight Offers Section
    if (offersLoading || flightOffers.isNotEmpty()) {
        Div({
            style {
                marginBottom(2.em)
            }
        }) {
            // Header with title and view all link
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    marginBottom(1.em)
                    property("@media (max-width: 640px)", "{ flex-direction: column; align-items: flex-start; gap: 0.5em; }")
                }
            }) {
                H2({
                    style {
                        fontSize(1.4.em)
                        fontWeight("800")
                        color(ThemeColors.textPrimary)
                        margin(0.px)
                    }
                }) { Text(WebStrings.t(WebStringKey.FEATURED_FLIGHT_OFFERS)) }

                A(href = "#/flights", attrs = {
                    style {
                        color(ThemeColors.primary)
                        fontSize(0.9.em)
                        fontWeight("600")
                        textDecoration("none")
                        cursor("pointer")
                    }
                    onClick {
                        it.preventDefault()
                        onNavigate("/flights")
                    }
                }) { Text(WebStrings.t(WebStringKey.VIEW_ALL)) }
            }

            // Loading state
            if (offersLoading) {
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        gridTemplateColumns("repeat(auto-fill, minmax(280px, 1fr))")
                        gap(1.em)
                        property("@media (max-width: 900px)", "{ grid-template-columns: repeat(2, minmax(0, 1fr)); }")
                        property("@media (max-width: 640px)", "{ grid-template-columns: 1fr; }")
                    }
                }) {
                    repeat(3) {
                        Div({
                            style {
                                backgroundColor(ThemeColors.skeleton)
                                height(200.px)
                                borderRadius(1.em)
                                property("animation", "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite")
                            }
                        }) {}
                    }
                }
            } else {
                // Flight offers grid - limited to 6 on homepage
            Div({
                    style {
                        display(DisplayStyle.Grid)
                        gridTemplateColumns("repeat(auto-fill, minmax(280px, 1fr))")
                        gap(1.em)
                    property("@media (max-width: 900px)", "{ grid-template-columns: repeat(2, minmax(0, 1fr)); }")
                    property("@media (max-width: 640px)", "{ grid-template-columns: 1fr; }")
                    }
                }) {
                    flightOffers.take(6).forEach { offer ->
                        FlightOfferCard(offer = offer, onNavigate = onNavigate)
                    }
                }
            }
        }
    }

    // Categories Section with nice cards
    if (categoriesLoading || categories.isNotEmpty()) {
        Div({
            style {
                marginTop(1.em)
                marginBottom(2.em)
            }
        }) {
            H2({
                style {
                    fontSize(1.4.em)
                    fontWeight("800")
                    color(ThemeColors.textPrimary)
                    marginBottom(1.em)
                }
            }) { Text(WebStrings.t(WebStringKey.BROWSE_CATEGORIES)) }

            if (categoriesLoading) {
            Div({
                    style {
                        display(DisplayStyle.Grid)
                        gridTemplateColumns("repeat(auto-fill, minmax(200px, 1fr))")
                        gap(1.em)
                    property("@media (max-width: 900px)", "{ grid-template-columns: repeat(2, minmax(0, 1fr)); }")
                    property("@media (max-width: 640px)", "{ grid-template-columns: 1fr; }")
                    }
                }) {
                    repeat(4) {
                        Div({
                            style {
                                backgroundColor(ThemeColors.skeleton)
                                height(120.px)
                                borderRadius(1.em)
                                property("animation", "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite")
                            }
                        }) {}
                    }
                }
            } else {
            Div({
                    style {
                        display(DisplayStyle.Grid)
                        gridTemplateColumns("repeat(auto-fill, minmax(200px, 1fr))")
                        gap(1.em)
                    property("@media (max-width: 900px)", "{ grid-template-columns: repeat(2, minmax(0, 1fr)); }")
                    property("@media (max-width: 640px)", "{ grid-template-columns: 1fr; }")
                    }
                }) {
                    categories.forEach { category ->
                        CategoryCard(category = category, onNavigate = onNavigate)
                    }
                }
            }
        }
    }
}

/**
 * Category card with icon and count
 */
@Composable
private fun CategoryCard(category: WebCategory, onNavigate: (String) -> Unit) {
    val icon = when (category.name.lowercase().trim()) {
        "flight", "flights" -> "✈️"
        "hotel", "hotels", "stay", "stays", "hotel & guesthouse" -> "🏨"
        "car", "cars", "rental" -> "🚗"
        "tour", "tours" -> "🌍"
        "package", "packages" -> "📦"
        "offer", "offers", "deal", "deals" -> "🏷️"
        else -> "✨"
    }
    
    Div({
        style {
            backgroundColor(ThemeColors.surface)
            borderRadius(1.em)
            padding(1.25.em)
            property("border", "1px solid #E5E5E5")
            property("box-shadow", "0 2px 8px rgba(0,0,0,0.04)")
            property("transition", "all 0.2s ease")
            cursor("pointer")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            minHeight(120.px)
        }
        onClick { onNavigate("/category/${category.name}") }
    }) {
        // Neutral dark icon circle
        Div({
            style {
                width(44.px)
                height(44.px)
                borderRadius(50.percent)
                backgroundColor(Color("#F2F2F2"))
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                marginBottom(0.6.em)
            }
        }) {
            Span({
                style {
                    fontSize(1.4.em)
                }
            }) { Text(icon) }
        }

        H3({
            style {
                fontSize(1.em)
                fontWeight("700")
                color(Color("#000000"))
                marginBottom(0.25.em)
                textAlign("center")
            }
        }) { Text(category.name) }

        Span({
            style {
                fontSize(0.8.em)
                color(Color("#AE7930"))
                fontWeight("600")
            }
        }) { Text(WebStrings.format(WebStringKey.ITEMS_COUNT, category.count.toString())) }
    }
}

/**
 * Flight offer card for home page display with booking form
 */
@Composable
private fun FlightOfferCard(offer: WebProduct, onNavigate: (String) -> Unit) {
    val airlineInfo = offer.airlineCode?.let { KNOWN_AIRLINES[it.uppercase()] }
    val airlineColor = airlineInfo?.second ?: "#a0a0a0"
    var showBookingForm by remember { mutableStateOf(false) }

    val hasDiscount = offer.discountPercent != null && offer.discountPercent > 0
    val discountText = offer.discountPercent?.let { "$it% OFF" } ?: offer.badge

    // Show booking form when requested
    if (showBookingForm) {
        ProductBookingForm(
            product = offer,
            onClose = { showBookingForm = false },
            onSuccess = { showBookingForm = false }
        )
    }

    Div({
        style {
            backgroundColor(ThemeColors.surface)
            borderRadius(1.em)
            overflow("hidden")
            property("border", "1px solid #333333")
            property("box-shadow", "0 2px 8px rgba(0,0,0,0.04)")
            property("transition", "all 0.25s ease")
            cursor("pointer")
            position(Position.Relative)
        }
        onClick { showBookingForm = true }
    }) {
        // Image container with badge overlay
        Div({
            style {
                position(Position.Relative)
                width(100.percent)
                height(140.px)
                overflow("hidden")
            }
        }) {
            offer.thumbnail?.let { url ->
                if (url.isNotBlank()) {
                    Img(src = url, attrs = {
                        style {
                            width(100.percent)
                            height(100.percent)
                            property("object-fit", "cover")
                            property("transition", "transform 0.3s ease")
                        }
                    })
                }
            } ?: run {
                Div({
                    style {
                        width(100.percent)
                        height(100.percent)
                        backgroundColor(Color(airlineColor))
                        property("background", "linear-gradient(135deg, $airlineColor 0%, #000000 100%)")
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                    }
                }) {
                    offer.airlineCode?.let { code ->
                        Span({
                            style {
                                color(ThemeColors.textOnDark)
                                fontSize(2.em)
                                fontWeight("800")
                                property("text-shadow", "0 2px 4px rgba(0,0,0,0.3)")
                            }
                        }) { Text(code) }
                    }
                }
            }

            // Discount badge
            discountText?.let { badge ->
                Div({
                    style {
                        position(Position.Absolute)
                        top(0.5.em)
                        left(0.5.em)
                        backgroundColor(
                            if (badge.contains("%")) ThemeColors.error else ThemeColors.primary
                        )
                        color(if (badge.contains("%")) ThemeColors.textOnDark else ThemeColors.textOnGold)
                        padding(0.3.em, 0.6.em)
                        borderRadius(0.4.em)
                        fontSize(0.7.em)
                        fontWeight("800")
                        property("box-shadow", "0 2px 4px rgba(0,0,0,0.2)")
                        property("text-transform", "uppercase")
                        property("letter-spacing", "0.04em")
                    }
                }) { Text(badge) }
            }

            // Airline logo overlay
            offer.airlineCode?.let { code ->
                Div({
                    style {
                        position(Position.Absolute)
                        bottom(0.5.em)
                        right(0.5.em)
                        width(32.px)
                        height(32.px)
                        borderRadius(50.percent)
                        backgroundColor(ThemeColors.surface)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        property("box-shadow", "0 2px 6px rgba(0,0,0,0.15)")
                    }
                }) {
                    Img(
                        src = "https://content.airhex.com/content/logos/airlines_${code}_50_50_t.png",
                        alt = airlineInfo?.first ?: code
                    ) {
                        style {
                            width(24.px)
                            height(24.px)
                            property("object-fit", "contain")
                        }
                    }
                }
            }
        }

        // Content
        Div({
            style {
                padding(1.em)
            }
        }) {
            // Airline name
            offer.airlineCode?.let { code ->
                Div({
                    style {
                        fontSize(0.75.em)
                        color(Color(airlineColor))
                        fontWeight("700")
                        marginBottom(0.25.em)
                        property("text-transform", "uppercase")
                        property("letter-spacing", "0.04em")
                    }
                }) { Text(airlineInfo?.first ?: code) }
            }

            // Title
            H3({
                style {
                    fontSize(1.em)
                    fontWeight("700")
                    color(ThemeColors.textPrimary)
                    marginBottom(0.4.em)
                    lineHeight("1.3")
                }
            }) { Text(offer.title ?: WebStrings.t(WebStringKey.FLIGHT_OFFER_LABEL)) }

            // Price section
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                }
            }) {
                Div {
                    // Original price with strikethrough
                    offer.originalPriceUsd?.let { original ->
                        if (original > (offer.priceUsd ?: 0)) {
                            Span({
                                style {
                                    color(ThemeColors.textSecondary)
                                    fontSize(0.8.em)
                                    textDecoration("line-through")
                                    marginRight(0.5.em)
                                }
                            }) { Text("USD $original") }
                        }
                    }

                    // Current price
                    Div({
                        style {
                            color(ThemeColors.success)
                            fontWeight("800")
                            fontSize(1.1.em)
                        }
                    }) {
                        val priceText = when {
                            offer.priceUsd != null && offer.priceUgx != null ->
                                "USD ${offer.priceUsd}"
                            offer.priceUsd != null -> "USD ${offer.priceUsd}"
                            offer.priceUgx != null -> "UGX ${offer.priceUgx}"
                            else -> WebStrings.t(WebStringKey.CONTACT_US)
                        }
                        Text(priceText)
                    }
                }

                // Book Now button
                Button({
                    style {
                        backgroundColor(ThemeColors.primary)
                        color(ThemeColors.textOnDark)
                        padding(0.5.em, 0.75.em)
                        borderRadius(0.5.em)
                        border(0.px)
                        cursor("pointer")
                        fontWeight("700")
                        fontSize(0.8.em)
                    }
                    onClick {
                        it.stopPropagation()
                        onNavigate("/checkout?productId=${offer.id}")
                    }
                }) {
                    Text(WebStrings.t(WebStringKey.BOOK_ACTION))
                }
            }
        }
    }
}
